import { LoggingHandler } from 'root/utils/errorReporting/LoggingHandler';
import { getFormattedTrackingString } from 'root/utils/tracking/BTFNavTracking/getFormattedTrackingString';
import getClickedElAncestorsText from 'root/utils/tracking/BTFNavTracking/getClickedElAncestorsText';

const channelsCallbackGroup = () => {
    const formCallback = (eventCategory, configLabelArray) => {
        return callableFunctionTemplate(
            { eventCategory, configLabelArray },
            concatFormLabel
        );
    };

    const fieldCallback = (eventCategory, configLabelArray) => {
        return callableFunctionTemplate(
            { eventCategory, configLabelArray },
            concatFormLabel
        );
    };

    const buttonCallback = (eventCategory, configLabelArray) => {
        return callableFunctionTemplate(
            { eventCategory, configLabelArray },
            concatButtonLabel
        );
    };

    const menuCallback = (eventCategory, configLabelArray) => {
        return callableFunctionTemplate(
            { eventCategory, configLabelArray },
            getMainBTFNavEventLabelText
        );
    };
    const secondaryMenuCallback = (eventCategory, configLabelArray) => {
        return callableFunctionTemplate(
            { eventCategory, configLabelArray },
            getSecondaryBTFNavEventLabelText
        );
    };
    const footerCallback = (eventCategory, configLabelArray) => {
        return callableFunctionTemplate(
            { eventCategory, configLabelArray },
            getFooterNavEventLabelText
        );
    };

    const regionAlertCallback = (eventCategory, configLabelArray) => {
        return callableFunctionTemplate(
            { eventCategory, configLabelArray },
            ({ labelEl }) => labelEl
        );
    };

    const businessTypeCookieSetCallback = (eventCategory, configLabelArray) => {
        return callableFunctionTemplate(
            { eventCategory, configLabelArray },
            getFormattedBusinessTypeLabel
        );
    };

    const cookieNotificationCallback = (eventCategory, configLabelArray) => {
        return (() => {
            return callableFunctionTemplate(
                { eventCategory, configLabelArray },
                ({ labelEl }) => labelEl
            );
        })();
    };

    const expertsCallback = (eventCategory, configLabelArray) => {
        return callableFunctionTemplate(
            { eventCategory, configLabelArray },
            ({ labelEl }) => labelEl
        );
    };

    const alertBannerCallback = (eventCategory, configLabelArray) => {
        return callableFunctionTemplate(
            { eventCategory, configLabelArray },
            truncateAlertBannerTitle
        );
    };

    const sliderCallback = (eventCategory, configLabelArray) => {
        return (() => {
            return callableFunctionTemplate(
                { eventCategory, configLabelArray },
                ({ labelEl }) => labelEl
            );
        })();
    };

    const pricingPaymentsToggleCallback = (eventCategory, configLabelArray) => {
        return (() => {
            return callableFunctionTemplate(
                { eventCategory, configLabelArray },
                ({ labelEl }) => labelEl
            );
        })();
    };

    const wizardFormElementCallback = (eventCategory, configLabelArray) => {
        return (() => {
            return callableFunctionTemplate(
                { eventCategory, configLabelArray },
                ({ labelEl }) => labelEl
            );
        })();
    };

    const wizardFormFieldCallback = (eventCategory, configLabelArray) => {
        return (() => {
            return callableFunctionTemplate(
                { eventCategory, configLabelArray },
                ({ labelEl }) => labelEl
            );
        })();
    };

    const wizardPricingCallback = (eventCategory, configLabelArray) => {
        return (() => {
            return callableFunctionTemplate(
                { eventCategory, configLabelArray },
                ({ labelEl }) => labelEl
            );
        })();
    };

    const btSelectionModuleCallback = (eventCategory, configLabelArray) => {
        return (() => {
            return callableFunctionTemplate(
                { eventCategory, configLabelArray },
                ({ labelEl }) => labelEl
            );
        })();
    };

    const dynamicLandingPagesCallback = (eventCategory, configLabelArray) => {
        return (() => {
            return callableFunctionTemplate(
                { eventCategory, configLabelArray },
                ({ labelEl }) => labelEl
            );
        })();
    };

    const tabClickForPanelChangeCallback = (eventCategory, configLabelArray) => {
        return (() => {
            return callableFunctionTemplate(
                { eventCategory, configLabelArray },
                ({ labelEl }) => labelEl
            );
        })();
    };

    return {
        formCallback,
        fieldCallback,
        buttonCallback,
        menuCallback,
        footerCallback,
        secondaryMenuCallback,
        regionAlertCallback,
        businessTypeCookieSetCallback,
        cookieNotificationCallback,
        expertsCallback,
        alertBannerCallback,
        sliderCallback,
        btSelectionModuleCallback,
        pricingPaymentsToggleCallback,
        wizardFormElementCallback,
        wizardFormFieldCallback,
        dynamicLandingPagesCallback,
        wizardPricingCallback,
        tabClickForPanelChangeCallback,
    };
};

const logErrorDetailsObj = {
    tags: ['gtm-tracking'],
    fileName: 'channelsCallbackGroup.js',
    additionalData: {
        location: window.location,
    },
};

const eventSendingObject = ({ eventCategory, eventLabel, eventValue = undefined }) => ({
    sendEvent: eventAction => {
        if (dataLayer) {
            dataLayer.push({
                event: 'GAEvent',
                eventCategory,
                eventAction,
                eventLabel,
                eventValue,
                nonInteraction: true,
            });
        } else {
            logErrorDetailsObj.searchTerm = 'missing-dataLayer';
            LoggingHandler().logError('No dataLayer was found', logErrorDetailsObj);
        }
    },
});

// eg: gtm.callableFunction(form).sendEvent('event-action')
const callableFunctionTemplate = (
    { eventCategory, configLabelArray = [] },
    modifierFunction
) => {
    return labelEl => {
        const eventLabel = modifierFunction({ labelEl, configLabelArray });
        return eventSendingObject({ eventCategory, eventLabel });
    };
};

// eg: gtm.silentFunction.sendEvent('event-action')
/*
const silentFunctionTemplate = ({ eventCategory, eventLabel }) => {
    return (() => {
        return eventSendingObject({ eventCategory, eventLabel });
    })();
};
*/

const getFormTrackingName = (formEl, field) => {
    const formTrackingName = formEl.dataset.formTrackingName;
    return formTrackingName ? formTrackingName : field.value;
};

const verifyFormField = (formEl, labelName) => {
    const fallbackObj = { value: 'empty' };
    const field = formEl.querySelector('input[name=' + labelName + ']');
    if (field && field.type === 'hidden' && field.value) {
        return field.name === 'form_name'
            ? getFormTrackingName(formEl, field)
            : field.value;
    }
    return fallbackObj.value;
};

const concatFormLabel = ({ labelEl, configLabelArray }) => {
    try {
        if (labelEl) {
            return configLabelArray
                .map(labelName => verifyFormField(labelEl, labelName))
                .join('|');
        } else {
            logErrorDetailsObj.searchTerm = 'missing-form-label-element';
            logErrorDetailsObj.tags.push('forms');
            LoggingHandler().logError('Label element does not exist', logErrorDetailsObj);
        }
    } catch (e) {
        logErrorDetailsObj.searchTerm = 'form-label-concatentation-error';
        logErrorDetailsObj.tags.push('forms');
        LoggingHandler().logError(e, logErrorDetailsObj);
    }
};
const concatButtonLabel = ({ labelEl, configLabelArray }) => {
    try {
        if (Object.entries(labelEl).length > 0) {
            return configLabelArray
                .map(a => labelEl[a])
                .filter(item => item !== '')
                .join('|');
        } else {
            logErrorDetailsObj.searchTerm = 'missing-button-element';
            LoggingHandler().logError(
                'Button element does not exist',
                logErrorDetailsObj
            );
        }
    } catch (e) {
        logErrorDetailsObj.searchTerm = 'button-label-concatenation-error';
        LoggingHandler().logError(e, logErrorDetailsObj);
    }
};

const getMainBTFNavEventLabelText = ({ labelEl }) => {
    if (labelEl && labelEl.innerText) {
        if (labelEl.closest('[data-navigation-group=main]')) {
            if (labelEl.closest('[data-navigation-platform=desktop]'))
                return getClickedElAncestorsText(labelEl);

            if (labelEl.closest('[data-navigation-platform=mobile]'))
                return getClickedElAncestorsText(labelEl);
        }
        return getFormattedTrackingString(labelEl.innerText);
    }
    return '';
};
const getSecondaryBTFNavEventLabelText = ({ labelEl }) => {
    if (labelEl && labelEl.innerText) {
        if (labelEl.closest('[data-navigation-group=secondary]')) {
            if (labelEl.closest('[data-navigation-platform=desktop]'))
                return getClickedElAncestorsText(labelEl);

            if (labelEl.closest('[data-navigation-platform=mobile]'))
                return getClickedElAncestorsText(labelEl);
        }
        return getFormattedTrackingString(labelEl.innerText);
    }
    return '';
};
const getFooterNavEventLabelText = ({ labelEl }) => {
    if (labelEl && labelEl.innerText) {
        if (labelEl.hasAttribute('data-navigation-level')) {
            const navLevel = labelEl.getAttribute('data-navigation-level');
            if (navLevel === 'parent') {
                return getFormattedTrackingString(labelEl.innerText);
            }
            if (navLevel === 'child') {
                const formattedChildText = getFormattedTrackingString(labelEl.innerText);

                const columnGroupEl = labelEl.closest('[data-footer-nav-column]');
                const parentEl = columnGroupEl
                    ? columnGroupEl.querySelector('[data-navigation-level=parent]')
                    : null;
                const formattedParentText = parentEl
                    ? getFormattedTrackingString(parentEl.innerText)
                    : '';

                return `${formattedParentText}>${formattedChildText}`;
            }
        }
        if (labelEl.closest('[data-language-flag-list]')) {
            return `region_code>${getFormattedTrackingString(labelEl.innerText)}`;
        }
        return getFormattedTrackingString(labelEl.innerText);
    }
    if (labelEl && labelEl.hasAttribute('data-footer-link-no-text')) {
        const readableLabel = labelEl.getAttribute('data-footer-link-no-text');
        return getFormattedTrackingString(readableLabel);
    }

    return '';
};

const getFormattedBusinessTypeLabel = ({ labelEl }) => {
    return labelEl.toLowerCase().replace(/\s|-/g, '_').trim();
};

const truncateAlertBannerTitle = ({ labelEl }) => {
    return labelEl ? labelEl.innerText.substring(0, 40) : '';
};

export { channelsCallbackGroup as default, concatFormLabel, eventSendingObject };
